import { Injectable } from '@angular/core';
import { BranchEndpointService } from './branch-endpoint.service';
import { PusherService, NewTokenData } from './pusher.service';

export type SoundBehaviourSetting = 'people_in_queue_from_0_to_1' | 'all_new_tokens';

export interface SoundSettings {
  playNewTokenSound: boolean;
  playServingSound: boolean;
  behaviour: SoundBehaviourSetting;
}

/**
 * SoundPlaybackService handles sound playback for token events via Pusher.
 * It subscribes to token events and plays sounds based on user settings.
 */
@Injectable({ providedIn: 'root' })
export class SoundPlaybackService {
  private readonly STORAGE_KEY = 'soundSettings';
  private readonly DEFAULT_SETTINGS: SoundSettings = {
    playNewTokenSound: false,
    playServingSound: false,
    behaviour: 'people_in_queue_from_0_to_1',
  };
  public settings: SoundSettings;
  private activeBranchId: number | string | null = null;

  constructor(
    private branchEndpointService: BranchEndpointService,
    private pusherService: PusherService,
  ) {
    this.loadSettings();
  }

  /**
   * Initialize if consumer does not already have a subscription to token events.
   * Should be called on page load for pages that want sound playback.
   * @param branchId
   */
  initialize(branchId: number | string) {
    this.loadSettings();
    this.subscribeToTokenEvents(branchId);
  }

  loadSettings() {
    try {
      const raw = window.localStorage.getItem(this.STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      const getBool = (val: any, fallback: boolean) => (typeof val === 'boolean' ? val : fallback);

      this.settings = {
        playNewTokenSound: getBool(parsed.playNewTokenSound, this.DEFAULT_SETTINGS.playNewTokenSound),
        playServingSound: getBool(parsed.playServingSound, this.DEFAULT_SETTINGS.playServingSound),
        behaviour: this.isValidBehaviour(parsed.behaviour) ? parsed.behaviour : this.DEFAULT_SETTINGS.behaviour,
      };
    } catch {
      console.warn('Failed to read sound settings from localStorage');
      this.settings = { ...this.DEFAULT_SETTINGS };
    }
  }

  /**
   * Handle incoming token events from pusher and play sounds accordingly.
   */
  public handleSoundPlayForTokenEvent = (payload: NewTokenData) => {
    if (!payload) return;

    const settings = this.settings;
    const { attributes } = payload.data;
    const { status, tokenNumber, totalPeopleInQueue } = attributes;

    // Pending token sound logic
    if (settings.playNewTokenSound && status === 'pending') {
      const shouldPlay =
        settings.behaviour === 'all_new_tokens' ||
        (settings.behaviour === 'people_in_queue_from_0_to_1' && totalPeopleInQueue === 1);

      if (shouldPlay) {
        this.branchEndpointService.playTokenEventSound(this.branchEndpointService.soundEffects.newToken);
      }
    }

    // Serving token sound logic
    else if (settings.playServingSound && status === 'serving') {
      this.branchEndpointService.playTokenEventSound(null, tokenNumber);
    }
  };

  disconnectTokenEvents() {
    this.pusherService.disconnectAllChannels();
    this.activeBranchId = null;
  }

  toggleNewTokenSound() {
    this.patchSettings({ playNewTokenSound: !this.settings.playNewTokenSound });
  }

  toggleServingSound() {
    this.patchSettings({ playServingSound: !this.settings.playServingSound });
  }

  setBehaviour(behaviour: SoundBehaviourSetting) {
    if (this.settings.behaviour === behaviour) return;
    this.patchSettings({ behaviour });
  }

  private subscribeToTokenEvents(branchId: number | string) {
    if (this.activeBranchId === branchId) return;
    if (this.activeBranchId !== null) {
      this.pusherService.disconnectAllChannels();
    }

    this.activeBranchId = branchId;
    this.pusherService.connectTokenBroadcast(branchId, this.handleSoundPlayForTokenEvent);
  }

  private isValidBehaviour(value: unknown): value is SoundBehaviourSetting {
    return value === 'people_in_queue_from_0_to_1' || value === 'all_new_tokens';
  }

  private updateSettings(settings: SoundSettings) {
    this.settings = settings;
    this.persistSettings(settings);
  }

  private patchSettings(patch: Partial<SoundSettings>) {
    this.updateSettings({ ...this.settings, ...patch });
  }

  private persistSettings(settings: SoundSettings): void {
    window.localStorage.setItem(this.STORAGE_KEY, JSON.stringify(settings));
  }
}
